// Support portal API + JWE encryption helpers
// Encrypts diagnostic bundles client-side using the Alceai public RSA key.
// Server unwraps via Key Vault HSM — plaintext never leaves the client unless decrypted by an authorised agent.

import { CompactEncrypt, importJWK } from 'jose';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://func-clara-demo.azurewebsites.net/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

// ============================================================
// Public RSA-4096 key (from kv-clara-demo / clara-support-jwe)
// kid = key version stored at submission time so the server can pick the right unwrap key on rotation.
// ============================================================
const SUPPORT_PUBLIC_JWK = {
  kty: 'RSA',
  use: 'enc',
  alg: 'RSA-OAEP-256',
  kid: 'https://kv-clara-demo.vault.azure.net/keys/clara-support-jwe/9bf0c66fe292433193476d97e3a2bfd6',
  e: 'AQAB',
  n: 'z5X95NCxKW6DiQbMlmep9jXNRiPAI5KwqN8JUGmbzQaI35GxEmaRlJfVqAHaoSwr5Q6v2jFGz32uPwGrDfMDTQibwQ7eWNz3M1AbjfQu-EpKBkG78MvBI_CVA_0CQP_sHHd1JGdR65Bx4IY9iqo-43JsCDyHlCEmQbkerFjosc24BZTHVHctRNpkcRzMARIR7LhCtk8J6EsYe9CCuzHSuzoLUrhecGAhicWcbHkefogtnFB3WelfqTKeuFIFW34her5DmsqC8UNFu8Q3zrHWnY-bs8eGOXDSbSaanq4CBFqs18D9-fnBgLhBL-v5e-VuEeHB7qswQ3SfW3GgRcZ-rG-kYlAc7GOR3VcKPW3Mtd8LCNfBElJax5wlMm4dt4c8mbK-psMlHgEOeBJzoHYLF0q0M7OC3f5aY2s0Bbyn0ce7wIXYaTIze5lXi_ZdjpGhZ7fl8QPjwUFme8KRYnQQUxi9501mJOqTlZXRKfEbFSqfR792QRP8Jddx9-R9hhJY7wnM_425bJPsG_2Dh19hPNIkJ2H5LQ68dMjrE8FYcdjvv1jHEp8jU8aQqa7RM07VQ8Yv2F6dVWRLmM3Vpia2fl9cH2405ogH-IpoCPCsc8jQuLweSfIX5s4ZkGGOotJSYrlthrF_kuId0LHOL7PYb1VFcwdUiY9RM1b4qOIEpRk',
};

// ============================================================
// Encrypt a UTF-8 string into a JWE compact serialization
// ============================================================
export async function encryptToJwe(plaintext: string): Promise<{ jweBundle: string; jweKeyId: string }> {
  const publicKey = await importJWK(SUPPORT_PUBLIC_JWK, 'RSA-OAEP-256');
  const enc = new TextEncoder().encode(plaintext);
  const jwe = await new CompactEncrypt(enc)
    .setProtectedHeader({ alg: 'RSA-OAEP-256', enc: 'A256GCM', kid: SUPPORT_PUBLIC_JWK.kid })
    .encrypt(publicKey);
  return { jweBundle: jwe, jweKeyId: SUPPORT_PUBLIC_JWK.kid };
}

// ============================================================
// SHA-256 hex hash (used for client email when posting replies)
// ============================================================
export async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input.toLowerCase()));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ============================================================
// API helpers
// ============================================================
async function apiFetch<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (API_KEY) headers['x-functions-key'] = API_KEY;
  const res = await fetch(url, {
    ...options,
    headers: { ...headers, ...(options.headers as Record<string, string> || {}) },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

// ============================================================
// Backend endpoints
// ============================================================
export const supportApi = {
  collectDiagnostics: (tenantId: string, tenantDisplayName: string | undefined, adminUpn: string, problemDescription: string, locale = 'fr-FR') =>
    apiFetch<{ markdown: string; piiMap: any[]; stats: any }>('/collect-diagnostics', {
      method: 'POST',
      body: JSON.stringify({ tenantId, tenantDisplayName, adminUpn, problemDescription, locale }),
    }),

  submitTicket: (payload: {
    tenantId: string;
    tenantDisplayName?: string;
    createdByUpnHash: string;
    contactEmail: string;
    summary: string;
    jweBundle: string;
    jweKeyId: string;
    piiMap: any[];
  }) =>
    apiFetch<{ ticketId: string; ticketNumber: string; status: string }>('/support-tickets', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Public (anonymous) endpoints — JWT-gated
  verifyToken: (token: string) =>
    apiFetch<{ valid: boolean; ticketId?: string; contactEmail?: string; purpose?: string; expiresAt?: string; error?: string }>(
      '/public/support/verify-token',
      { method: 'POST', body: JSON.stringify({ token }) }
    ),

  getPublicTicket: (token: string) =>
    apiFetch<{
      ticketId: string;
      ticketNumber: string;
      summary: string;
      status: string;
      tenantDisplayName?: string;
      createdAt: string;
      lastAgentMessage?: { sequenceNumber: number; preview: string; createdAt: string };
      thread: Array<{ sequenceNumber: number; fromParty: 'agent' | 'client'; preview: string; createdAt: string }>;
      canReply: boolean;
    }>('/public/support/ticket', { method: 'POST', body: JSON.stringify({ token }) }),

  addReply: (token: string, message: string, jweBundle?: string, jweKeyId?: string) =>
    apiFetch<{ ticketId: string; ticketNumber: string; sequenceNumber: number; newStatus: string }>(
      '/public/support/reply',
      {
        method: 'POST',
        body: JSON.stringify({ token, message, jweBundle, jweKeyId }),
      }
    ),
};
